import type { CoreApiEvent, DriveEvent } from '@protontech/drive-sdk';
import type { EventSubscription } from '@protontech/drive-sdk/dist/internal/events/interface';

import type { EventManager } from '@proton/shared/lib/eventManager/eventManager';
import { wait } from '@proton/shared/lib/helpers/promise';
import { getItem } from '@proton/shared/lib/helpers/storage';

import { DriveEventType, getDrive, getDriveForPhotos } from '../../../sdk';
import { logging } from '../../logging';
import {
    type BusDriverClient,
    type BusDriverEvent,
    type BusDriverEventListener,
    type BusDriverEventMap,
    BusDriverEventName,
} from './BusDriverTypes';
import { sendErrorReport } from './errorHandling';

const logger = logging.getLogger('bus-driver');
const logDebug = (label: string, rest: string | Record<string, unknown> = '') => {
    logger.debug(`${label}: ${JSON.stringify(rest)}`);
};
const logWarning = (label: string, rest: string | Record<string, unknown> = '') => {
    logger.warn(`${label}: ${JSON.stringify(rest)}`);
};

/**
 * BusDriver - Provides a centralized and type-safe way to emit and listen to application events.
 *
 * Provides a centralized way to emit and listen to folder-related actions throughout the application.
 * Each event type has a specific shape that is enforced at compile time.
 *
 * @example
 * // Basic usage with the singleton instance
 * import { getBusDriver, BusDriverEventName } from './BusDriver';
 *
 * const eventBus = getBusDriver();
 *
 * // Subscribe to specific events
 * eventBus.subscribe(BusDriverEventName.TRASH_NODES_OPTIMISTIC, (event) => {
 *    event.uids.forEach((uid) => {
 *        useFolderStore.getState().removeItem(uid);
 *    });
 * });
 *
 * // Subscribe to all events
 * eventBus.subscribe(BusDriverEventName.ALL, (event) => {
 *     switch (event.type) {
 *         case BusDriverEventName.TRASH_NODES_OPTIMISTIC:
 *             event.uids.forEach((uid) => {
 *                 useFolderStore.getState().removeItem(uid);
 *             });
 *             break;
 *         case BusDriverEventName.CREATED_NODES:
 *             event.nodes.forEach((node) => {
 *                 useFolderStore.getState().addNode(node);
 *             });
 *             break;
 *     }
 * });
 *
 * // Later, unsubscribe
 * unsubscribe();
 *
 * // Emit events
 * eventBus.emit({
 *   type: BusDriverEventName.TRASH_NODES_OPTIMISTIC,
 *   uids: ['123', '456']
 * });
 *
 *
 * @example
 * // Different event types with their specific shapes
 * import { getBusDriver, BusDriverEventName } from './BusDriver';
 *
 * const eventBus = getBusDriver();
 *
 * // Trash items event
 * eventBus.emit({
 *   type: BusDriverEventName.TRASH_NODES_OPTIMISTIC,
 *   uids: ['1', '2']
 * });
 *
 * // Create nodes event
 * eventBus.emit({
 *   type: BusDriverEventName.CREATED_NODES,
 *   nodes: [getNodeEntity(maybeNode)]
 * });

 */
class BusDriver {
    private listeners = new Map<string, BusDriverEventListener<BusDriverEvent>[]>();

    private myFilesRootFolderTreeEventScopeId: string | undefined;

    private photosRootFolderTreeEventScopeId: string | undefined;

    private treeEventSubscriptions = new Map<
        string,
        {
            subscription: Promise<EventSubscription>;
            contexts: Set<string>;
        }
    >();

    private driveEventSubscription:
        | {
              unsubscribeCoreEventManager: () => void;
              contexts: Set<string>;
          }
        | undefined;

    private debugMode: boolean;

    // TODO: Create an helper to get the value
    constructor() {
        const debugValue = getItem('proton-drive-debug', 'false');
        this.debugMode = debugValue === 'true';
    }

    async emit(event: BusDriverEvent, driveClient: BusDriverClient): Promise<void> {
        // Emit is called right away on the client side after the action is
        // performed. The listeners might fetch the latest data from the server
        // which is not replicated yet. We wait for a small delay that is not
        // noticeable to the user and solves majority of the cases. It is not
        // perfect and in the case of a longer delay, the user might see still
        // a stale data. API event eventually fixes the issue.
        // Example: transfer manager uploads file and emits the event, file
        // browser processes the event by loading the node metadata to show
        // the file in the list. However, the uploader just committed the file
        // to the server and the file is still draft in replicas. Not waiting
        // causes that the file is not visible and user needs to wait for the
        // API event.
        // 500ms looks as a good compromise, but can be tweaked if needed.
        await wait(500);

        await this.emitInternal(event, driveClient);
    }

    private async emitInternal(event: BusDriverEvent, driveClient: BusDriverClient): Promise<void> {
        const eventListeners = this.listeners.get(event.type) || [];
        const allEventListeners = this.listeners.get(BusDriverEventName.ALL) || [];

        const allListeners = [...eventListeners, ...allEventListeners];

        logger.debug(`Emitting action event ${event.type}`);

        // TODO: We need to verify the logic when we will have persisted storage
        await Promise.allSettled(
            allListeners.map(async (listener) => {
                try {
                    await listener(event, driveClient);
                } catch (error) {
                    sendErrorReport(error);
                }
            })
        );
    }

    /**
     * Subscribes a listener to a specific event type.
     *
     * @param eventType - The type of event to listen for
     * @param listener - The callback function to execute when the event is emitted
     * @returns A function that can be called to unsubscribe the listener
     */
    subscribe<K extends keyof BusDriverEventMap>(
        eventType: K,
        listener: (event: BusDriverEventMap[K], driveClient: BusDriverClient) => Promise<void>
    ): () => void {
        if (!this.listeners.has(eventType)) {
            this.listeners.set(eventType, []);
        }
        const listeners = this.listeners.get(eventType);
        if (listeners) {
            if (listeners.some((l) => l === listener)) {
                throw new Error(`The same event listener for ${eventType} event has been added twice`);
            }
            listeners.push(listener as BusDriverEventListener<BusDriverEvent>);
        }

        return () => this.unsubscribe(eventType, listener);
    }

    /**
     * Unsubscribes a listener from a specific event type.
     * This method is private and should only be called through the function returned by subscribe.
     *
     * @param eventType - The type of event to stop listening for
     * @param listener - The callback function to remove
     */
    private unsubscribe<K extends keyof BusDriverEventMap>(
        eventType: K,
        listener: (event: BusDriverEventMap[K], driveClient: BusDriverClient) => Promise<void>
    ): void {
        const eventListeners = this.listeners.get(eventType);
        if (eventListeners) {
            const index = eventListeners.indexOf(listener as BusDriverEventListener<BusDriverEvent>);
            if (index > -1) {
                eventListeners.splice(index, 1);
            }
        }
    }

    /**
     * Removes all listeners from all event types.
     * Useful for cleanup in tests or resetting the event bus state.
     */
    clear(): void {
        this.listeners.clear();
    }

    /**
     * Subscribe to my updates sdk events (Basically user main volume events)
     * @param context - A unique context identifier (e.g., 'trashContainer', 'folderView')
     */
    async subscribeSdkEventsMyUpdates(context: string): Promise<void> {
        const drive = getDrive();
        let treeEventScopeId = this.myFilesRootFolderTreeEventScopeId;
        try {
            if (!treeEventScopeId) {
                const rootFolder = await drive.getMyFilesRootFolder();
                treeEventScopeId = rootFolder.treeEventScopeId;
                this.myFilesRootFolderTreeEventScopeId = rootFolder.treeEventScopeId;
            }
            this.subscribeSdkEventsScope(treeEventScopeId, context);
        } catch (error) {
            sendErrorReport(error);
        }
    }

    /**
     * Subscribe to my updates photo-sdk events
     */
    async subscribePhotosEventsMyUpdates(context: string): Promise<void> {
        const drive = getDriveForPhotos();
        let treeEventScopeId = this.photosRootFolderTreeEventScopeId;
        try {
            if (!treeEventScopeId) {
                const rootFolder = await drive.getMyPhotosRootFolder();
                treeEventScopeId = rootFolder.treeEventScopeId;

                this.photosRootFolderTreeEventScopeId = treeEventScopeId;
            }
            this.subscribePhotosEventsScope(treeEventScopeId, context);
        } catch (error) {
            sendErrorReport(error);
        }
    }

    /**
     * Unsubscribe from  Subscribe to my updates sdk events for a given context
     * Only reset myFilesRootFolderTreeEventScopeId when no contexts remain
     * @param context - The context identifier that was used when subscribing
     */
    async unsubscribeSdkEventsMyUpdates(context: string): Promise<void> {
        if (!this.myFilesRootFolderTreeEventScopeId) {
            logWarning(
                `[BusDriver] Trying to unsubscribe to SdkEventsMyUpdates without having the treeEventScopeId for it`,
                { context }
            );
            return;
        }

        await this.unsubscribeSdkEventsScope(this.myFilesRootFolderTreeEventScopeId, context, 'drive');

        // We only reset in case there is no more subscription to it
        const key = this.getTreeSubscriptionKey(this.myFilesRootFolderTreeEventScopeId, 'drive');
        if (!this.treeEventSubscriptions.get(key)) {
            this.myFilesRootFolderTreeEventScopeId = undefined;
        }
    }

    /**
     * Unsubscribe from photo-sdk events for a given context
     * Only reset photosRootFolderTreeEventScopeId when no contexts remain
     */
    async unsubscribePhotosEventsMyUpdates(context: string): Promise<void> {
        const treeEventScopeId = this.photosRootFolderTreeEventScopeId;

        if (!treeEventScopeId) {
            logWarning(
                `[BusDriver] Trying to unsubscribe to PhotosEventsMyUpdates without having the treeEventScopeId for it`,
                { context }
            );
            return;
        }

        await this.unsubscribeSdkEventsScope(treeEventScopeId, context, 'photos');

        const key = this.getTreeSubscriptionKey(treeEventScopeId, 'photos');
        if (!this.treeEventSubscriptions.get(key)) {
            this.photosRootFolderTreeEventScopeId = undefined;
        }
    }

    /**
     * Subscribe to a specific tree event scope with context tracking
     * @param treeEventScopeId - The tree event scope ID to subscribe to
     * @param context - A unique context identifier (e.g., 'trashContainer', 'folderView')
     */
    subscribeSdkEventsScope(treeEventScopeId: string, context: string) {
        const key = this.getTreeSubscriptionKey(treeEventScopeId, 'drive');
        const existing = this.treeEventSubscriptions.get(key);

        if (existing) {
            if (existing.contexts.has(context)) {
                if (this.debugMode) {
                    logDebug('[BusDriver] Given context already exist for SDK scope subscription', {
                        context,
                        totalContexts: existing.contexts.size,
                        allContexts: Array.from(existing.contexts),
                    });
                }
                return;
            }
            existing.contexts.add(context);
            if (this.debugMode) {
                logDebug('[BusDriver] Added context to existing SDK scope subscription', {
                    treeEventScopeId,
                    context,
                    totalContexts: existing.contexts.size,
                    allContexts: Array.from(existing.contexts),
                });
            }
            return;
        }

        const drive = getDrive();
        const subscription = drive.subscribeToTreeEvents(treeEventScopeId, async (event: DriveEvent) => {
            await this.handleSdkEvent(event, drive);
        });

        this.treeEventSubscriptions.set(key, {
            subscription,
            contexts: new Set([context]),
        });

        if (this.debugMode) {
            logDebug('[BusDriver] Subscribed to new SDK scope events', {
                treeEventScopeId,
                context,
                totalScopes: this.treeEventSubscriptions.size,
            });
        }
    }
    /**
     * Subscribe to a specific tree event scope with context tracking
     * @param treeEventScopeId - The tree event scope ID to subscribe to
     * @param context - A unique context identifier (e.g., 'trashContainer', 'folderView')
     */
    subscribePhotosEventsScope(treeEventScopeId: string, context: string) {
        const key = this.getTreeSubscriptionKey(treeEventScopeId, 'photos');
        const existing = this.treeEventSubscriptions.get(key);

        if (existing) {
            if (existing.contexts.has(context)) {
                if (this.debugMode) {
                    logDebug('[BusDriver] Given context already exist for Photos scope subscription', {
                        context,
                        totalContexts: existing.contexts.size,
                        allContexts: Array.from(existing.contexts),
                    });
                }
                return;
            }
            existing.contexts.add(context);
            if (this.debugMode) {
                logDebug('[BusDriver] Added context to existing Photos scope subscription', {
                    treeEventScopeId,
                    context,
                    totalContexts: existing.contexts.size,
                    allContexts: Array.from(existing.contexts),
                });
            }
            return;
        }

        const photosClient = getDriveForPhotos();
        const subscription = photosClient.subscribeToTreeEvents(treeEventScopeId, async (event: DriveEvent) => {
            await this.handleSdkEvent(event, photosClient);
        });

        this.treeEventSubscriptions.set(key, {
            subscription,
            contexts: new Set([context]),
        });

        if (this.debugMode) {
            logDebug('[BusDriver] Subscribed to new Photos scope events', {
                treeEventScopeId,
                context,
                totalScopes: this.treeEventSubscriptions.size,
            });
        }
    }

    /**
     * Unsubscribe from a specific tree event scope for a given context
     * Only disposes the actual subscription when no contexts remain
     * @param treeEventScopeId - The tree event scope ID to unsubscribe from
     * @param context - The context identifier that was used when subscribing
     */
    async unsubscribeSdkEventsScope(
        treeEventScopeId: string,
        context: string,
        client: 'drive' | 'photos' = 'drive'
    ): Promise<void> {
        const key = this.getTreeSubscriptionKey(treeEventScopeId, client);
        const existing = this.treeEventSubscriptions.get(key);
        if (!existing) {
            logWarning(
                '[BusDriver] Trying to unsubscribe to SdkEventsScope without having the treeEventScopeId for it',
                {
                    treeEventScopeId,
                    context,
                    client,
                }
            );
            return;
        }

        existing.contexts.delete(context);

        if (existing.contexts.size === 0) {
            await existing.subscription.then(({ dispose }) => dispose());
            this.treeEventSubscriptions.delete(key);
            if (this.debugMode) {
                logDebug('[BusDriver] Unsubscribed from SDK scope events', {
                    treeEventScopeId,
                    remainingScopes: this.treeEventSubscriptions.size,
                });
            }
        } else if (this.debugMode) {
            logDebug('[BusDriver] Removed context from SDK scope subscription', {
                treeEventScopeId,
                removedContext: context,
                remainingContexts: existing.contexts.size,
                allContexts: Array.from(existing.contexts),
            });
        }
    }

    /**
     * Subscribe to general drive sdk events.
     *
     * Accepts the host application's core event manager so the SDK does not
     * open a second polling connection. Raw core events are fed into both the
     * drive and photos SDK instances via their push-based experimental API.
     *
     * @param context - A unique context identifier (e.g., 'trashContainer', 'folderView')
     * @param coreEventManager - The host application's core event manager
     */
    async subscribeSdkDriveEvents(context: string, coreEventManager: EventManager<unknown>): Promise<void> {
        if (this.driveEventSubscription) {
            if (this.driveEventSubscription.contexts.has(context)) {
                if (this.debugMode) {
                    logDebug('[BusDriver] Given context already exist for SDK drive events subscription', {
                        context,
                        totalContexts: this.driveEventSubscription.contexts.size,
                        allContexts: Array.from(this.driveEventSubscription.contexts),
                    });
                }
                return;
            }
            this.driveEventSubscription.contexts.add(context);
            if (this.debugMode) {
                logDebug('[BusDriver] Added context to existing SDK drive events subscription', {
                    context,
                    totalContexts: this.driveEventSubscription.contexts.size,
                    allContexts: Array.from(this.driveEventSubscription.contexts),
                });
            }
        } else {
            const drive = getDrive();
            const photosClient = getDriveForPhotos();

            const unsubscribeCoreEventManager = coreEventManager.subscribe(async (rawEvent) => {
                // General core EventManager loop contains all CoreApiEvent fields (Refresh, EventID, DriveShareRefresh)
                // plus extras the sdk ignores, so the cast is safe.
                const event = rawEvent as CoreApiEvent;
                const driveEvents = await drive.experimental.processCoreEvent(event);
                const photosEvents = await photosClient.experimental.processCoreEvent(event);
                await Promise.all([
                    ...driveEvents.map((event) => this.handleSdkEvent(event, drive)),
                    ...photosEvents.map((event) => this.handleSdkEvent(event, photosClient)),
                ]);
            });

            this.driveEventSubscription = {
                unsubscribeCoreEventManager,
                contexts: new Set([context]),
            };
            if (this.debugMode) {
                logDebug('[BusDriver] Subscribed to SDK drive events', { context });
            }
        }
    }

    private getTreeSubscriptionKey(treeEventScopeId: string, client: 'drive' | 'photos') {
        return `${client}:${treeEventScopeId}`;
    }

    /**
     * Subscribe to general drive sdk events
     * @param context - A unique context identifier (e.g., 'trashContainer', 'folderView')
     */
    async unsubscribeSdkDriveEvents(context: string): Promise<void> {
        const existing = this.driveEventSubscription;
        if (!existing) {
            logWarning(
                '[BusDriver] Trying to unsubscribe to general drive sdk event without having the treeEventScopeId for it',
                {
                    context,
                }
            );
            return;
        }

        existing.contexts.delete(context);

        if (existing.contexts.size === 0) {
            existing.unsubscribeCoreEventManager();
            this.driveEventSubscription = undefined;
            if (this.debugMode) {
                logDebug('[BusDriver] Unsubscribed from SDK drive events');
            }
        } else if (this.debugMode) {
            logDebug('[BusDriver] Removed context from SDK drive events subscription', {
                removedContext: context,
                remainingContexts: existing.contexts.size,
                allContexts: Array.from(existing.contexts),
            });
        }
    }

    /**
     * Handle incoming SDK events and emit corresponding BusDriverEvents
     */
    private async handleSdkEvent(event: DriveEvent, driveClient: BusDriverClient): Promise<void> {
        if (this.debugMode) {
            logDebug('[BusDriver] Handling SDK event', {
                eventType: event.type,
                nodeUid: 'nodeUid' in event ? event.nodeUid : undefined,
            });
        }

        try {
            switch (event.type) {
                case DriveEventType.NodeCreated:
                    await this.emitInternal(
                        {
                            type: BusDriverEventName.CREATED_NODES,
                            items: [
                                {
                                    uid: event.nodeUid,
                                    parentUid: event.parentNodeUid,
                                    isTrashed: event.isTrashed,
                                    isShared: event.isShared,
                                },
                            ],
                        },
                        driveClient
                    );
                    break;
                case DriveEventType.NodeUpdated:
                    await this.emitInternal(
                        {
                            type: BusDriverEventName.UPDATED_NODES,
                            items: [
                                {
                                    uid: event.nodeUid,
                                    parentUid: event.parentNodeUid,
                                    isTrashed: event.isTrashed,
                                    isShared: event.isShared,
                                },
                            ],
                        },
                        driveClient
                    );
                    break;
                case DriveEventType.NodeDeleted:
                    await this.emitInternal(
                        { type: BusDriverEventName.DELETED_NODES, uids: [event.nodeUid] },
                        driveClient
                    );
                    break;
                case DriveEventType.SharedWithMeUpdated:
                    await this.emitInternal({ type: BusDriverEventName.REFRESH_SHARED_WITH_ME }, driveClient);
                    break;
            }
        } catch (error) {
            sendErrorReport(error);
        }
    }
}

let BusDriverInstance: BusDriver | null = null;

/**
 * Gets the singleton instance of the BusDriver.
 * Creates a new instance if one doesn't exist.
 *
 * @returns The BusDriver singleton instance
 */
export function getBusDriver(): BusDriver {
    if (!BusDriverInstance) {
        BusDriverInstance = new BusDriver();
    }
    return BusDriverInstance;
}
